import React, { memo } from "react";
import cx from "classnames";
import Text, { H4 } from "@ui/Text";
import t from "../../../../../../../../i18n";
import { editorHeader } from "../_styles/header";
import { ResourceSelector } from "./component/ResourceSelector";
import { GUIEditorContext, useCreateGUIEditorContext } from "../../../../../hook/context/gui";
import { TimeScaleSetting } from "./component/TimeScaleSetting";
import Details from "@ui/Details";
import { ErrorDetails } from "../_styles/error";
import BlankStateV2 from "@ui/BlankStateV2";
import { SimpleGraph } from "@ui/Illustration/react";
import { formSectionHidden, formSectionItemFull } from "./_styles/form";
import { useDashboardEditorContext } from "../../../../../hook/context/editor";
import { YAxisGroupByUnitToggle } from "./component/YAxisGroupByUnitToggle";
import { LayerSettings } from "./component/LayerSettings";
import { editorSection, editorSectionSeparator, editorSectionTitle } from "../_styles/section";
import { DisplaySetting } from "./component/DisplaySetting";
import { BlockDataTitle } from "../BlockDataTitle";
import { BlockPosition } from "../BlockPosition";
import { BlockDateRangeSelector } from "../BlockDateRangeSelector";
import { DisplayInvalidatedDataPointsToggle } from "./component/DisplayInvalidatedDataPointsToggle";

export const PlotEditor = memo(
  ({ projectId, preset }: { projectId: string; preset: "scatter" | "line" }) => {
    const editor = useDashboardEditorContext();
    const context = useCreateGUIEditorContext(preset);

    const hasEncodings =
      (context.block?.config.layers || []).length > 0 &&
      context.block?.config.layers.some(
        layer => layer.encoding.x.identifier.length > 0 || layer.encoding.y.identifier.length > 0,
      );

    if (!context.block) return null;

    return (
      <GUIEditorContext.Provider value={context}>
        <header className={cx(editorHeader, editorSection)}>
          <H4>
            {t("project.dashboard.editor.gui.title", t("project.dashboard.block.kind", "PLOT"))}
          </H4>
        </header>

        <section className={editorSection}>
          <BlockPosition block={context.block} />
        </section>

        <section className={editorSection}>
          <BlockDataTitle key={context.block.id} block={context.block} />
        </section>

        <div className={editorSectionSeparator} />

        <section className={editorSection}>
          <ResourceSelector
            projectId={projectId}
            className={cx({ [formSectionHidden]: context.focusedEncoding })}
          />
          {hasEncodings ? (
            <>
              {preset === "line" && (
                <YAxisGroupByUnitToggle
                  className={cx({ [formSectionHidden]: context.focusedEncoding })}
                />
              )}
              <LayerSettings projectId={projectId} layers={context.block.config.layers} />
            </>
          ) : (
            <BlankStateV2
              picto={<SimpleGraph size="m" />}
              title="Pas de donnée"
              subtitle="Ajoutez des données à votre graphe."
              className={formSectionItemFull}
            />
          )}
        </section>

        {hasEncodings && (
          <>
            <div className={editorSectionSeparator} />

            <section className={editorSection}>
              <Text type="body1SemiBold" className={editorSectionTitle}>
                {t("project.dashboard.editor.gui.section.period.title")}
              </Text>
              <BlockDateRangeSelector
                projectId={projectId}
                block={context.block}
                defaultPreset="ALL"
              />
              <TimeScaleSetting />
            </section>

            <div className={editorSectionSeparator} />

            <section className={editorSection}>
              <Text type="body1SemiBold" className={editorSectionTitle}>
                {t("project.dashboard.editor.gui.section.display.title")}
              </Text>
              <DisplaySetting projectId={projectId} />
              <DisplayInvalidatedDataPointsToggle />
            </section>
          </>
        )}
        {editor?.error && <Details className={ErrorDetails} text={editor?.error} />}
      </GUIEditorContext.Provider>
    );
  },
);
